import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

const tTitle = "Add to Deal";
const tDeal = "Deal";
const tCancel = "Cancel";
const tAdd = "Add";
const tLoading = "Loading deals...";
const tNoDeals = "No existing deals are available to add this prospect to.";
const tHelper = "Select an existing deal to add this prospect to.";

export interface AddProspectToDealOption {
  id: string;
  name: string;
}

export interface AddProspectToDealModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (dealId: string) => Promise<void>;
  deals: AddProspectToDealOption[];
  isLoading?: boolean;
  isPending?: boolean;
  prospectName?: string;
}

export const AddProspectToDealModal = ({
  open,
  onClose,
  onSubmit,
  deals,
  isLoading = false,
  isPending = false,
  prospectName,
}: AddProspectToDealModalProps) => {
  const [selectedDeal, setSelectedDeal] = useState<AddProspectToDealOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const busy = isPending || isSubmitting;

  useEffect(() => {
    if (!open) {
      setSelectedDeal(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedDeal?.id) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(selectedDeal.id);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={busy ? undefined : onClose} open={open}>
      <DialogTitle>{tTitle}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          {prospectName ? `Add ${prospectName} to an existing deal.` : tHelper}
        </Typography>
        {isLoading ? (
          <Box sx={{ alignItems: "center", display: "flex", gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{tLoading}</Typography>
          </Box>
        ) : deals.length === 0 ? (
          <Typography color="text.secondary">{tNoDeals}</Typography>
        ) : (
          <Autocomplete<AddProspectToDealOption>
            disabled={busy}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedDeal(value)}
            options={deals}
            renderInput={(params) => <TextField {...params} label={tDeal} margin="normal" />}
            value={selectedDeal}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {tCancel}
        </Button>
        <Button
          disabled={busy || isLoading || !selectedDeal?.id}
          onClick={() => void handleSubmit()}
          variant="contained"
        >
          {tAdd}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
